import { useScheduleEditorCreateItemMutation, useSetTagsForResourceMutation } from '@graphql/generated';
import { useCallback } from 'react';
import type { ScheduleEditorData } from '../ScheduleEditor.types';
import { ScheduleFormDisplay, ScheduleFormDisplaySkeleton } from './form/ScheduleFormDisplay';
import React from 'react';
import type { ScheduleEditorProps } from '../ScheduleEditor';
import { EMPTY_FORM_DATA } from '../ScheduleEditor.constants';
import { formatDateAsISO8601TZ, parseISO8601WithTimezone, useGetEventTimezoneAndDates } from './form/hooks/useGetEventTimezone';
import { utcToZonedTime } from 'date-fns-tz';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { addError } from '@shared/utils/logger';

const RESOURCE_TYPE = 'SCHEDULE_ITEM';

export const AddScheduleItem = (props: ScheduleEditorProps & { mode: 'add' }) => {
  const { eventId, initialStartTime, onSuccess, onError, onCancel, refetch } = props;
  const [addItem, { loading: isSaving }] = useScheduleEditorCreateItemMutation();
  const [setTagsForResource] = useSetTagsForResourceMutation();
  const { timezone, eventDate, loading } = useGetEventTimezoneAndDates();

  const handleSubmit = useCallback(
    async (data: ScheduleEditorData) => {
      try {
        const results = await addItem({
          variables: {
            payload: {
              eventId,
              startTimeISO8601TZ: formatDateAsISO8601TZ(data.startTime, data.timezone),
              endTimeISO8601TZ: formatDateAsISO8601TZ(data.endTime, data.timezone),
              name: data.name,
              description: data.description,
              isPublic: true,
              virtualEventLink: data.virtualEventUrl,
              dressCode: data.dressCode,
              location: data.location,
              placeId: data.placeId,
              showMap: data.showMap,
            },
          },
          refetchQueries: refetch,
          awaitRefetchQueries: true,
        });

        if (results.data?.createEventScheduleItem) {
          const newScheduleItemId = results.data.createEventScheduleItem.id;

          // Set tags for the newly created schedule item
          await setTagsForResource({
            variables: {
              eventId,
              tagIds: data.tags.map(t => t.id),
              resourceId: newScheduleItemId,
              resourceType: RESOURCE_TYPE,
            },
            refetchQueries: ['GetTagsByResource'],
          });

          onSuccess(results.data.createEventScheduleItem);
        } else {
          onError?.(results.errors);
        }
      } catch (error) {
        addError(error, { context: 'AddScheduleItem', message: 'Failed to create schedule item' });
        onError?.(error);
      }
    },
    [addItem, setTagsForResource, eventId, onError, onSuccess, refetch],
  );

  const parsedInitial = initialStartTime ? parseISO8601WithTimezone(initialStartTime) : null;

  const defaultTime = (() => {
    if (parsedInitial) return parsedInitial.datetime;
    if (eventDate) return eventDate;
    if (!timezone) return new Date();

    try {
      return utcToZonedTime(new Date(), timezone);
    } catch (error) {
      addError(error, { context: 'AddScheduleItem', message: `Invalid timezone fallback: ${timezone}` });
      return new Date();
    }
  })();

  // When prefilling from initialStartTime, use the source event's timezone so the prefilled time is not shifted
  const defaultTimezone = parsedInitial?.timezone ?? timezone ?? '';

  return (
    <SkeletonGroup
      placeholder={<ScheduleFormDisplaySkeleton />}
      isReady={!loading}
    >
      <ScheduleFormDisplay
        isSaving={isSaving}
        data={{
          ...EMPTY_FORM_DATA,
          // Empty string is required by TimeZonePicker and validation logic
          timezone: defaultTimezone,
          startTime: defaultTime,
          endTime: defaultTime,
        }}
        onSubmit={handleSubmit}
        onCancel={() => {
          onCancel?.();
        }}
        eventId={eventId}
      />
    </SkeletonGroup>
  );
};
